import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { get } from 'lodash';
import { connect } from 'react-redux';
import withAlert from '../../hoc/withAlert';
import { startLogin, startGetProfile } from '../../actions/webmaster';
import validateInput from '../../../validations/signUp';
import { redirectToOffice } from '../../utils/utils';
import InlineError from '../Forms/InlineError';
import PasswordField from '../Forms/PasswordField';
import Countdown from 'react-countdown-now';

/**
 * Helpers
 */
import {
  authList,
  countDownRender,
  getPlaceholder,
} from '../../components/TwoFactorAuth/twoFactorAuthHelpers';
import {
  getUrlParameter,
  Cookies,
  shouldDisableRecaptcha,
  detectAutofill,
} from '../../utils/utils';
import { webmaster } from '../../services/ApiService';

class AffiliateEmailSignInForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      isLoading: false,
      showRecoverySuccessMessage: false,
      activateMessageState: {
        show: false,
        message: '',
      },
      data: {
        email: '',
        password: '',
        recaptchaToken: '',
        code: '',
      },
      isTwoFactor: false,
      timestamp: null,
      verification: {},
      showResendVerificationEmail: false,
      showVerificationEmailResent: false,
      isEmailFilled: false,
      isPasswordFilled: false,
      isEmailAutoFilled: false,
      isPasswordAutoFilled: false,
    };

    this.recaptcha = null;
    this.emailInputRef = React.createRef();
    this.passwordInput = null;
    this.passwordWrapperRef = React.createRef();
    this.isUpdatingFromAutofill = false;
    this.autofillCheckDone = false;
  }

  setPasswordInputRef = (element) => {
    this.passwordInput = element;
  };

  updateFieldState = (fieldName, value, isAutoFilled = false) => {
    const currentDataValue = this.state.data[fieldName];

    if (
      currentDataValue === value &&
      this.state[`is${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}AutoFilled`] ===
        isAutoFilled
    ) {
      return;
    }

    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        [fieldName]: value,
      },
      [`is${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}Filled`]: !!value,
      [`is${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}AutoFilled`]: isAutoFilled,
    }));
  };

  onChange = (e) => {
    if (this.isUpdatingFromAutofill) {
      return;
    }

    const fieldName = e.target.name;
    const value = e.target.value;

    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        [fieldName]: value,
      },
      [`is${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}Filled`]: !!value,
      [`is${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}AutoFilled`]: false,
    }));
  };

  handleAutoFillAnimation = (e) => {
    if (e.animationName === 'onAutoFillStart') {
      const fieldName = e.target.name;
      this.isUpdatingFromAutofill = true;

      setTimeout(() => {
        if (fieldName === 'email' && this.emailInputRef.current) {
          const emailValue = this.emailInputRef.current.value;
          this.updateFieldState('email', emailValue, true);
        } else if (fieldName === 'password' && this.passwordInput) {
          const passwordValue = this.passwordInput.value;
          this.updateFieldState('password', passwordValue, true);
        }

        setTimeout(() => {
          this.isUpdatingFromAutofill = false;
        }, 100);
      }, 50);
    }
  };

  checkAutofillOnLoad = async () => {
    if (this.autofillCheckDone) {
      return;
    }

    const checks = [];

    if (this.emailInputRef.current) {
      checks.push(
        detectAutofill(this.emailInputRef.current, 300).then((isAutofilled) => ({
          field: 'email',
          isAutofilled,
          value: this.emailInputRef.current.value,
        })),
      );
    }

    if (this.passwordInput) {
      checks.push(
        detectAutofill(this.passwordInput, 300).then((isAutofilled) => ({
          field: 'password',
          isAutofilled,
          value: this.passwordInput.value,
        })),
      );
    }

    const results = await Promise.all(checks);

    let hasUpdates = false;
    for (const result of results) {
      if (
        result.isAutofilled &&
        !this.state[`is${result.field.charAt(0).toUpperCase() + result.field.slice(1)}AutoFilled`]
      ) {
        this.updateFieldState(result.field, result.value, true);
        hasUpdates = true;
      }
    }

    if (hasUpdates) {
      this.forceUpdate();
    }

    this.autofillCheckDone = true;
  };

  quickAutofillCheck = () => {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(async () => {
          await this.checkAutofillOnLoad();
          resolve();
        }, 100);
      });
    });
  };

  activateUserAfterRegistration = (hash) => {
    webmaster
      .activate(hash)
      .then(() => {
        if (window.location.href.includes('devstage')) {
          redirectToOffice('https://office.devstage.ttplace.online/', 0);
        } else {
          redirectToOffice('https://my.affiliate.tutorplace.ru/', 0);
        }
      })
      .catch((err) =>
        this.setState({
          activateMessageState: {
            show: true,
            message: this.props.intl.formatMessage({
              id: 'affiliate.activate.error',
              defaultMessage: 'An error occured during registation process.',
            }),
          },
        }),
      );
  };

  componentDidMount() {
    const recovery_success = getUrlParameter('recovery_success');
    const activate_hash = getUrlParameter('activate_hash');
    recovery_success && this.setState({ showRecoverySuccessMessage: true });
    activate_hash && this.activateUserAfterRegistration(activate_hash);

    if (this.emailInputRef.current) {
      this.emailInputRef.current.addEventListener('animationstart', this.handleAutoFillAnimation);
    }

    if (this.passwordInput) {
      this.passwordInput.addEventListener('animationstart', this.handleAutoFillAnimation);
    }

    this.quickAutofillCheck();

    setTimeout(() => {
      if (!this.autofillCheckDone) {
        this.checkAutofillOnLoad();
      }
    }, 300);

    setTimeout(() => {
      if (!this.autofillCheckDone) {
        this.checkAutofillOnLoad();
      }
    }, 600);
  }

  componentWillUnmount() {
    if (this.emailInputRef.current) {
      this.emailInputRef.current.removeEventListener(
        'animationstart',
        this.handleAutoFillAnimation,
      );
    }

    if (this.passwordInput) {
      this.passwordInput.removeEventListener('animationstart', this.handleAutoFillAnimation);
    }
  }

  handleErrorTypes = (err) => {
    const connectErrType = get(err, 'response.data.error.message.type');
    if (connectErrType && connectErrType === 'api.account.master_join_error') {
      return true;
    }
    return false;
  };

  sendData = (data) => {
    this.props
      .startLogin(data)
      .then(({ profile = {} } = {}) => {
        if (profile.auth && profile.redirectUrl) {
          if (window.location.hostname.includes('s1.')) {
            redirectToOffice('https://my.s1.stage.offerrum.com', 0);
          } else {
            window.gtag_report_conversion();
            redirectToOffice(profile.redirectUrl, 0);
          }
        } else if (this.props.webmaster.errors) {
          const {
            code,
            verification: { sessionId, validTill, type },
          } = this.props.webmaster;
          const timestamp = validTill ? validTill * 1000 : null;
          const { isTwoFactor } = this.state;

          if (code === 401) {
            this.setState(() => ({
              isLoading: false,
            }));
            if (!isTwoFactor) {
              this.setState((prevState) => ({
                isTwoFactor: true,
                timestamp,
                verification: {
                  sessionId,
                  type,
                },
              }));
            } else {
              this.setState(() => ({
                timestamp,
                errors: {
                  response: this.props.webmaster.errors.message,
                },
              }));
            }
          } else if (this.props.webmaster.errors.type === 'api.authorization.password_recovery') {
            const message = (
              <span className="form-error">
                <FormattedMessage
                  id="affiliate.auth.password_expired.text"
                  defaultMessage="Your password has expired. For security reasons, you should use the"
                />{' '}
                <button className="btn-inline" onClick={() => this.props.changeForm('recovery')}>
                  <FormattedMessage
                    id="affiliate.auth.password_expired.link"
                    defaultMessage="password recovery service"
                  />
                </button>
              </span>
            );
            this.setState(() => ({
              isLoading: false,
              errors: {
                response: message,
              },
            }));
          } else {
            if (this.props.webmaster.errors.type === 'api.authorization.approve_email') {
              this.setState(() => ({
                showResendVerificationEmail: true,
              }));
            }
            this.setState(() => ({
              isLoading: false,
              errors: {
                response: this.props.webmaster.errors.message,
              },
            }));
          }
        }
      })
      .catch((err) => {
        const slaveConnectionError = this.handleErrorTypes(err);
        if (slaveConnectionError) {
          redirectToOffice(undefined, 0);
        }
      });
  };

  onSubmit = (e) => {
    e.preventDefault();

    const errors = validateInput(this.state.data);
    this.setState({ errors });

    if (Object.keys(errors).length === 0) {
      const { reacaptcha_sitekey } = this.props;
      this.setState(() => ({ isLoading: true }));
      if (shouldDisableRecaptcha()) {
        this.verifyCallback(null);
      } else {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(reacaptcha_sitekey.v3, { action: 'registration' })
            .then((recaptchaToken) => {
              this.verifyCallback(recaptchaToken);
            });
        });
      }
    }
  };

  verifyCallback = (recaptchaToken) => {
    const isRecaptchaDisabled = shouldDisableRecaptcha();
    const payload = {};
    const { verification, data } = this.state;

    if (data.code) {
      switch (verification.type) {
        case 1:
          payload['sms_session_id'] = verification.sessionId;
          payload['sms_code'] = data.code;
          break;
        case 2:
          payload['token'] = data.code;
          break;
        case 4:
          payload['telegram_code'] = data.code;
          payload['telegram_session_id'] = verification.sessionId;
          break;
        default:
          break;
      }
    }
    this.setState(
      (prevState) => ({
        ...prevState,
        data: {
          ...prevState.data,
          recaptchaToken: isRecaptchaDisabled ? '' : recaptchaToken,
        },
      }),
      () => {
        const {
          data: { email, password },
        } = this.state;
        if (email && password) {
          const redirectUrl =
            localStorage.getItem('redirect_url') || Cookies.get('redirect_url') || undefined;
          const requestData = {
            ...this.state.data,
            redirectUrl,
            ...payload,
          };
          if (isRecaptchaDisabled) {
            delete requestData.recaptchaToken;
          }
          this.sendData(requestData);
        }
      },
    );
  };

  resendVerificationEmailCaptchaCB = (token) => {
    webmaster
      .passwordRecovery(this.state.data.email, token)
      .then(() => this.setState({ showVerificationEmailResent: true }))
      .catch((err) => {
        this.setState((prev) => ({
          errors: {
            ...prev.errors,
            response: err.response?.data?.error?.message?.message || (
              <FormattedMessage id="main.error" defaultMessage="Error" />
            ),
          },
          showVerificationEmailResent: false,
        }));
      });
  };

  resendVerificationEmail = (e) => {
    e.preventDefault();
    const { reacaptcha_sitekey } = this.props;

    if (shouldDisableRecaptcha()) {
      this.resendVerificationEmailCaptchaCB();
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(reacaptcha_sitekey.v3, { action: 'recovery' })
        .then((recaptchaToken) => {
          this.resendVerificationEmailCaptchaCB(recaptchaToken);
        });
    });
  };

  render() {
    const {
      data,
      errors,
      isTwoFactor,
      timestamp,
      verification,
      showRecoverySuccessMessage,
      activateMessageState,
      isLoading,
      isEmailFilled,
      isPasswordFilled,
      isEmailAutoFilled,
      isPasswordAutoFilled,
    } = this.state;
    const responseErrors = this.state.errors.response;

    return (
      <form onSubmit={this.onSubmit} className={'form-hidden show'}>
        {showRecoverySuccessMessage && (
          <div className="success-recovery-text">
            <FormattedMessage
              tagName="b"
              id="affiliate.recovery.success"
              defaultMessage="The new password is set. Authorize with it."
            />
          </div>
        )}
        {activateMessageState.show && (
          <div className="success-recovery-text">
            <b>{activateMessageState.message}</b>
          </div>
        )}

        <div className={errors.email ? 'form-group has-error' : 'form-group'}>
          <label
            htmlFor="email"
            className={isEmailFilled || isEmailAutoFilled ? 'label label-upper' : 'label'}
          >
            <FormattedMessage id="affiliate.login.mail" defaultMessage="Email" />
          </label>
          <input
            ref={this.emailInputRef}
            id="email"
            className="form-control"
            type="email"
            name="email"
            autoComplete="email"
            value={data.email}
            onChange={this.onChange}
            tabIndex="1"
            required
          />
          {errors.email && <InlineError text={errors.email} />}
        </div>

        <div className={errors.password ? 'form-group has-error' : 'form-group'}>
          <PasswordField
            inputRef={this.setPasswordInputRef}
            showRecoveryLink
            id="password"
            className="form-control"
            name="password"
            value={data.password}
            onChange={this.onChange}
            tabIndex="2"
            autoComplete="current-password"
            label={<FormattedMessage id="affiliate.login.password" defaultMessage="Password" />}
            isLabelUpper={isPasswordFilled || isPasswordAutoFilled}
            required
          />
          {errors.password && <InlineError text={errors.password} />}
        </div>

        {isTwoFactor && (
          <Fragment>
            <div className={'form-group'}>
              <label htmlFor="code" className={!data.code ? 'label' : 'label label-upper'}>
                <FormattedMessage
                  id="notifications.sms.modal.field"
                  defaultMessage="Verification code"
                />
              </label>
              <input
                id="code"
                className="form-control"
                type="text"
                name="code"
                autoComplete="off"
                value={data.code}
                onChange={this.onChange}
                placeholder={getPlaceholder(this.props.intl, verification.sessionId)}
                tabIndex="1"
                required
              />
            </div>
            <p>
              <FormattedMessage
                id="notifications.sms.modal.description"
                defaultMessage="Two-factor authorization is enabled on your account, you can find this code in the mobile application - Authenticator"
              />
            </p>
            {timestamp && (
              <p className="session-timer-container">
                <Countdown date={timestamp} zeroPadTime={2} renderer={countDownRender} />
              </p>
            )}
          </Fragment>
        )}

        {responseErrors && <span className="form-error">{responseErrors}</span>}

        {this.state.showResendVerificationEmail ? (
          <div className="form-resend-email">
            {this.state.showVerificationEmailResent ? (
              <div className="resend-success-text">
                <FormattedMessage
                  id="affiliate.activate.resend_confitmation.sent"
                  defaultMessage="Confirmation email resent"
                />
              </div>
            ) : null}
            <FormattedMessage
              id="affiliate.activate.resend_confitmation.missing"
              defaultMessage="Haven't received an email?"
            />{' '}
            <button className="btn-inline" onClick={this.resendVerificationEmail}>
              <FormattedMessage
                id="affiliate.activate.resend_confitmation.resend"
                defaultMessage="Send again"
              />
            </button>
          </div>
        ) : null}

        <div className="form-controls">
          <button
            disabled={isLoading}
            type="submit"
            className="btn btn-block btn-primary"
            tabIndex="3"
          >
            {isLoading ? (
              <span className="loading-spinner loading-spinner-btn" />
            ) : (
              <FormattedMessage id="main.login" defaultMessage="Login" />
            )}
          </button>
        </div>
      </form>
    );
  }
}

const mapStateToProps = (state) => ({
  webmaster: state.webmaster,
  reacaptcha_sitekey: state.app.authSettings.recaptcha,
});

const mapDispatchToProps = (dispatch) => ({
  startLogin: (data) => dispatch(startLogin(data)),
  startGetProfile: () => dispatch(startGetProfile()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withAlert(injectIntl(AffiliateEmailSignInForm)));
